import { useMemo } from 'react'

import { formatDate } from '@domain-group/javascript-utils'

import { useWatchEntityUpdates } from '@domain-group/react-hooks'

import { StatusBadge, Table, Text } from '@domain-group/suit-theme'

import getAdCampaignChannel from '@functions/adCampaign'
import getStatusBadgeProps from '@functions/statusBadge'

import useSelector from '@hooks/useSelector'

import { AdCampaignModel } from '@models/types'

import type { EntitiesState } from '@redux/entities'

import Actions from './Actions'

const buildColumns = (entities: EntitiesState) => {
  return [
    {
      title: 'Title',
      dataKey: 'adCampaignName',
      cellProps: {
        verticalAlign: 'middle',
      },
    },
    {
      title: 'AIM#',
      dataKey: 'campaignId',
      cellProps: {
        textAlign: 'center',
        verticalAlign: 'middle',
        width: 40,
      },
      render: (_: any, adCampaign: AdCampaignModel) => {
        const { campaignId, referenceId } = adCampaign

        return (
          <Text variant="ellipsis">
            {campaignId ?? referenceId}
          </Text>
        )
      },
    },
    {
      title: 'Deployment Stack',
      dataKey: 'deploymentStack',
      cellProps: {
        textAlign: 'center',
        verticalAlign: 'middle',
      },
      render: (_: any, adCampaign: AdCampaignModel) => (
        <Text variant="ellipsis">
          {adCampaign.campaignId != null ? 'V2' : 'V1'}
        </Text>
      ),
    },
    {
      title: 'Start',
      dataKey: 'startsAt',
      cellProps: {
        textAlign: 'center',
        verticalAlign: 'middle',
        width: 40,
      },
      render: (startsAt: string) => (
        <StatusBadge
          boxProps={{
            backgroundColor: 'backgroundColor',
            borderColor: 'lineColor',
            color: 'black',
          }}
          text={formatDate(startsAt, 'ISO8601', 'dd/MM/yyyy')}
        />
      ),
    },
    {
      title: 'Channel',
      dataKey: 'adChannelTypeId',
      cellProps: {
        textAlign: 'center',
        verticalAlign: 'middle',
        width: 40,
      },
      render: (_: any, adCampaign: AdCampaignModel) => (
        <Text variant="ellipsis">
          {getAdCampaignChannel(adCampaign)}
        </Text>
      ),
    },
    {
      title: 'Ad Account',
      dataKey: 'adAccountId',
      cellProps: {
        textAlign: 'center',
        verticalAlign: 'middle',
        width: 40,
      },
      render: (adAccountId: number) => {
        return (
          <Text variant="ellipsis">
            {adAccountId}
          </Text>
        )
      },
    },
    {
      title: 'Status',
      dataKey: 'status',
      cellProps: {
        textAlign: 'center',
        verticalAlign: 'middle',
        width: 40,
      },
      render: (_: any, adCampaign: AdCampaignModel) => {
        const { status } = adCampaign

        const statusBadgeProps = getStatusBadgeProps(status)

        return (
          <StatusBadge
            boxProps={{ borderWidth: 2, width: '92px' }}
            ghost
            {...statusBadgeProps}
          />
        )
      },
    },
    {
      title: 'Actions',
      dataKey: 'id',
      cellProps: {
        key: 'actions',
        textAlign: 'center',
        verticalAlign: 'middle',
        width: 20,
      },
      render: (adCampaignId: number) => <Actions adCampaignId={adCampaignId} />,
    },
  ]
}

const watchEntityKeys = ['adCampaigns', 'campaigns']

interface AdCampaignsTableProps {
  adCampaigns: AdCampaignModel[],
}

const AdCampaignsTable = (props: AdCampaignsTableProps) => {
  const { adCampaigns } = props

  const {
    updatedEntities: {
      adCampaigns: adCampaignsUpdatedAt,
      campaigns: campaignsUpdatedAt,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  const entities = useSelector(reduxState => reduxState.entities)

  const memoColumns = useMemo(() => (
    buildColumns(entities)
  ), [adCampaignsUpdatedAt, campaignsUpdatedAt])

  return (
    <Table
      boxProps={{ border: 'none', maxHeight: 1600, overflowY: 'auto' }}
      columns={memoColumns}
      data={adCampaigns}
      scroll={{ x: 800 }}
      stickyHeader
    />
  )
}

export default AdCampaignsTable
